// SHARED — keep in sync across pro/care/backbar/academy (canonical: workspace/shared-web-auth/). Extract to @rootegra/auth later.
//
// One-time bridge from the old per-app localStorage session to the shared
// `.rootegra.com` cookie session, so nobody gets signed out by the storage
// move. Call migrateLegacySession(client) once on boot, before (or alongside)
// the app's first getSession().

import type { SupabaseClient } from '@supabase/supabase-js';

/** supabase-js default storage key for project lhtjiixwfjxeezhxwegs. */
export const LEGACY_SESSION_STORAGE_KEY = 'sb-lhtjiixwfjxeezhxwegs-auth-token';

/**
 * If the client has no session but the old localStorage entry exists, adopt
 * it via setSession (which persists it into the client's current storage —
 * the shared cookie on *.rootegra.com).
 *
 * The localStorage entry is deliberately KEPT on success: if this deploy is
 * rolled back, the old build can still find its session. (The refresh token
 * inside it may rotate once the cookie session refreshes; that is an accepted
 * trade-off — worst case the rolled-back build asks the user to sign in.)
 * Sign-out should remove it: call clearLegacySession() there.
 *
 * Never throws. Returns true only if a legacy session was adopted.
 */
export async function migrateLegacySession(client: SupabaseClient): Promise<boolean> {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return false;

    const { data } = await client.auth.getSession();
    if (data.session) return false; // already signed in — nothing to migrate

    const raw = window.localStorage.getItem(LEGACY_SESSION_STORAGE_KEY);
    if (!raw) return false;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false; // unparseable — leave it alone, do nothing
    }

    const access_token = (parsed as { access_token?: unknown })?.access_token;
    const refresh_token = (parsed as { refresh_token?: unknown })?.refresh_token;
    if (typeof access_token !== 'string' || typeof refresh_token !== 'string') return false;

    const { error } = await client.auth.setSession({ access_token, refresh_token });
    if (error) return false;

    // Success: keep the localStorage entry (mixed-version rollback safety).
    return true;
  } catch {
    return false; // never throw — a failed migration just means "sign in again"
  }
}

/** Storage-key marker auth-js uses for the in-flight PKCE code verifier. */
const PKCE_VERIFIER_KEY_FRAGMENT = 'code-verifier';

/** True if a cookie with exactly this name exists on the current document. */
function cookieExists(name: string): boolean {
  return document.cookie
    .split(';')
    .some((part) => part.trim().slice(0, name.length + 1) === `${name}=`);
}

/**
 * ONE-TIME TRANSIENT-WINDOW MITIGATION for the localStorage → cookie storage
 * cutover, for PKCE apps (care's magic links, backbar's magic links).
 *
 * The hole it plugs: a magic link REQUESTED by the pre-cutover build writes
 * its PKCE code verifier into localStorage. If the user then OPENS that link
 * after the cookie build deploys, the new build's cookie storage can't see the
 * verifier, so the ?code= exchange fails and the user has to request a fresh
 * link. To bridge the window, copy any in-flight verifier from localStorage
 * into an equivalent cookie on `.rootegra.com` BEFORE the Supabase client is
 * created, so @supabase/ssr's cookie storage finds it under the same key.
 *
 * Rules:
 * - Synchronous, and MUST run before createBrowserClient (call it from the
 *   factory's cookie branch).
 * - Never overwrites an existing cookie of the same name — a verifier already
 *   in the cookie was written by the new build and is newer than any
 *   localStorage leftover.
 * - The localStorage copy is deliberately KEPT (rollback safety — same policy
 *   as migrateLegacySession).
 * - Raw (non-`base64-`-prefixed) cookie values pass through @supabase/ssr's
 *   decodeChunkedCookieValue untouched (verified against 0.10.3), so the
 *   verifier is stored URI-encoded as-is. Verifiers are short — no chunking.
 * - Short-lived (24h ≥ any OTP link expiry); auth-js removes the key after a
 *   successful exchange. Never throws.
 *
 * Once every session predates the cookie cutover this is dead code and can be
 * removed together with migrateLegacySession.
 */
export function preserveLegacyPkceVerifiers(): void {
  try {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !('localStorage' in window)
    ) {
      return;
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith('sb-') || !key.includes(PKCE_VERIFIER_KEY_FRAGMENT)) continue;
      const value = window.localStorage.getItem(key);
      if (!value) continue;
      if (cookieExists(key)) continue; // cookie build already wrote a newer one
      document.cookie =
        `${key}=${encodeURIComponent(value)}; domain=.rootegra.com; path=/; ` +
        'max-age=86400; secure; samesite=lax';
    }
  } catch {
    /* never throw — worst case the user requests a fresh link */
  }
}

/** Remove the legacy localStorage session. Call on sign-out. Never throws. */
export function clearLegacySession(): void {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    window.localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
